import React, { useEffect, useMemo, useState } from 'react';

import { format, parseISO } from 'date-fns';
import QueryString from 'qs';
import { omit } from 'ramda';
import { useQuery, useQueryClient } from 'react-query';
import { useAsyncDebounce } from 'react-table';

import brokersApi from 'api/brokers';
import { FETCH_BROKERS, FETCH_LISTINGS } from 'constants/queries';
import { Broker } from 'types/Broker';

import { BrokersProps } from './Brokers.props';
import BrokersView from './Brokers.view';

// Default table options for pagination
export const TABLE_OPTIONS = {
  page: 1,
  limit: 20,
};

// Utility function to capitalize the first letter of a string
const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const BrokersContainer = (props: BrokersProps): JSX.Element => {
  // State to manage table options (pagination, filters, etc.)
  const [tableOptions, setTableOptions] =
    useState<Record<string, any>>(TABLE_OPTIONS);

  // Query client instance for cache management
  const queryClient = useQueryClient();

  // Fetch brokers data using react-query
  const { data, refetch, isFetching } = useQuery(FETCH_BROKERS, async () =>
    brokersApi.getAll(
      `${
        props.location.search ? props.location.search + '&' : '?'
      }${QueryString.stringify(tableOptions)}`
    )
  );

  // Parse query parameters from URL
  const queries = useMemo(
    () => QueryString.parse(new URLSearchParams(location.search).toString()),
    [location.search]
  );

  // Reset page number to 1 when a new search query is entered
  useEffect(() => {
    setTableOptions((opts) => ({ ...opts, page: 1 }));
  }, [queries.search]);

  // Debounced search function to avoid excessive API calls
  const onSearchKey = useAsyncDebounce(
    (searchKey: string) =>
      props.history.push({
        pathname: props.location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...omit(['search'], queries),
            ...(searchKey ? { search: searchKey } : {}),
          }).toString(),
      }),
    600 // 600ms debounce delay
  );

  // Function to update the URL when a broker is selected
  const setListing = (broker?: Broker) =>
    props.history.push({
      pathname: props.location.pathname,
      search:
        '?' +
        new URLSearchParams({
          ...omit(['id'], queries),
          ...(broker ? { id: broker.id } : {}),
        }).toString(),
    });

  // Format brokers data for the UI
  const brokers = useMemo(
    () =>
      (data?.data || []).map((broker) => ({
        ...broker,
        name: `${broker.firstName} ${broker.lastName}`,
        createdAt: format(parseISO(broker.createdAt), 'dd/MM/yyyy H:mm a'),
        status: capitalize(broker.status),
      })),
    [data]
  );

  // Extract metadata (pagination info) from API response
  const meta = useMemo(
    () =>
      data?.meta || {
        count: 0,
        limit: 10,
        page: 1,
      },
    [data]
  );

  // Function to apply filters by updating URL parameters
  const onSetFilter = (value: string, key: string) =>
    props.history.push({
      pathname: props.location.pathname,
      search: '?' + new URLSearchParams({ [key]: value }).toString(),
    });

  // Function to clear all filters
  const onClearFilter = () =>
    props.history.push({
      pathname: props.location.pathname,
    });

  // Refetch brokers data when location search or table options change
  useEffect(() => {
    queryClient.cancelQueries([FETCH_LISTINGS]); // Cancel any ongoing listing queries
    setTimeout(refetch, 300); // Delay refetch slightly to optimize performance
  }, [props.location.search, tableOptions]);

  return (
    <BrokersView
      setListing={setListing}
      data={brokers}
      meta={meta}
      onSearchKey={onSearchKey}
      isLoading={isFetching}
      onClearFilter={onClearFilter}
      onSetFilter={onSetFilter}
      tableOptions={tableOptions}
      setTableOptions={setTableOptions}
      refetch={refetch}
    />
  );
};

export default BrokersContainer;
